/* eslint-disable babel/camelcase */
/* eslint-disable max-classes-per-file */
/* eslint camelcase: 0 */
import moment from "moment";

import { DateService } from "./dates.service";
import { moduleMap, systemCheckKeyMap } from "./maps/keyMaps";
import { systemCheckValueMap } from "./maps/valuemaps";
import { fromEntries, validateNumericValue } from "./misc.service";
import { underscoresToSpaces } from "./text-formatter.service";
import { BaseSortableByStartTime } from "./timeline.service";

export class SanitizedEvent extends BaseSortableByStartTime {
  constructor({ timestamp, customBlocklist = [], vehicle_id, tzoffset, ...rest }) {
    super({ timestamp, ...rest });
    // eslint-disable-next-line no-return-assign
    Object.entries(rest).forEach(([k, v]) => (this[underscoresToSpaces(k)] = v));
    this.customBlocklist = customBlocklist;
    this["Vehicle ID"] = vehicle_id;
    this.timezone = tzoffset;
  }

  static compareTime = (a, b) => {
    const aLessRecent = moment(a.timestamp).isBefore(b.timestamp);
    return aLessRecent ? 1 : -1;
  };

  // list of keys we don't want represented in the UI
  static blocklist = [
    "bike locked",
    "bms version",
    "sc version",
    "md version",
    "start_time",
    "end_time",
    "toRepresentation",
    "customBlocklist",
    "keyMap",
    "valueMap",
    "getDescriptionByModuleAndId",
  ];

  static baseKeyMap = {
    timezone: "timezone offset",
  };

  keyMap = {};

  valueMap = {};

  getDescriptionByModuleAndId = (module, id) => {
    if (this.valueMap[module]) {
      return this.valueMap[module][id];
    }
    return null;
  };

  toRepresentation = () => {
    const event = { ...this };
    new Set([...this.customBlocklist, ...SanitizedEvent.blocklist]).forEach(
      (key) => delete event[key],
    );
    const items = Object.entries(event)
      // eslint-disable-next-line no-unused-vars
      .filter(([k, v]) => v !== undefined)
      .sort(([aKey], [bKey]) => (aKey < bKey ? -1 : 1));

    const decodeItems = ([key, value]) => {
      const newValue =
        typeof value === "object" && value
          ? fromEntries(Object.entries(value).map(decodeItems))
          : this.getDescriptionByModuleAndId(key, value) || value;

      const keyMap = { ...SanitizedEvent.baseKeyMap, ...this.keyMap };
      const newKey = keyMap[key] || key;
      return [newKey, newValue];
    };

    const decodedItems = items.map(decodeItems.bind(this));
    return fromEntries(decodedItems);
  };
}

export class Hazard extends SanitizedEvent {
  constructor(hazardMap, { ...event }) {
    super(event);
    this.valueMap = hazardMap;
  }

  description = this.getDescriptionByModuleAndId(this.module, this.id) || undefined;
}

export class Fault extends SanitizedEvent {
  constructor(faultMap, { ...event }) {
    const customBlocklist = [];
    super({
      customBlocklist,
      ...event,
    });
    this.valueMap = faultMap;
  }

  description = this.getDescriptionByModuleAndId(this.module, this.id) || undefined;

  static isWithinNinetySeconds = (fault) => {
    const now = Date.now();
    const timestamp = DateService.convert(fault.timestamp).valueOf();
    return now - timestamp < 90000;
  };
}

export class Warning extends SanitizedEvent {
  constructor(warningMap, { ...event }) {
    super(event);
    this.valueMap = warningMap;
  }

  description = this.getDescriptionByModuleAndId(this.module, this.id) || undefined;
}

export class SystemCheck extends SanitizedEvent {
  constructor(
    faultMap,
    {
      cell_strength,
      up_time,
      throttle_pos,
      speed_limit,
      charging,
      batt_voltage,
      batt_soc,
      ...event
    },
  ) {
    const customBlocklist = [];
    // TODO: figure out how to translate these
    const customFields = {
      throttle_position: validateNumericValue(throttle_pos) ? `${throttle_pos}%` : undefined,
      present_faults: SystemCheck.mapLatchedFaults(faultMap, event.latched_faults) || "None",
      cell_strength: validateNumericValue(cell_strength) ? `${cell_strength} dBm` : undefined,
      up_time: validateNumericValue(up_time) ? `${up_time} s` : undefined,
      speed_limit: validateNumericValue(speed_limit) ? `${speed_limit} km/h` : undefined,
      charging_now: charging,
      battery_voltage: validateNumericValue(batt_voltage) ? `${batt_voltage} V` : undefined,
      battery_state_of_charge: validateNumericValue(batt_soc) ? `${batt_soc}%` : undefined,
    };
    super({ customBlocklist, ...customFields, ...event });
  }

  type = "System check";

  valueMap = systemCheckValueMap;

  keyMap = systemCheckKeyMap;

  static mapLatchedFaults = (faultMap, faults = []) => {
    if (faults.length === 0) return "None";
    return faults.map((fault) => {
      const { module } = fault;
      if (!fault.faults) return "";

      return fault.faults
        .map((id) => `${moduleMap[module]}: ${faultMap[module][id]}`)
        .join("<br />");
    });
  };
}
